import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authService } from '../services/authService'
import { TokenManager, SessionManager } from '../utils/auth'
import { useFeedback } from './FeedbackContext'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sessionWarning, setSessionWarning] = useState(null)
  const { showError, showSuccess } = useFeedback()

  // Clear error after a timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [error])

  const clearTokens = useCallback(() => {
    TokenManager.clearTokens()
    SessionManager.clearTimers()
    setUser(null)
    setSessionWarning(null)
  }, [])

  // Session management
  const handleSessionExpiring = useCallback((secondsRemaining) => {
    setSessionWarning({
      type: 'expiring',
      message: `Your session will expire in ${Math.floor(secondsRemaining / 60)} minutes. Would you like to extend it?`,
      secondsRemaining
    })
  }, [])

  const handleSessionExpired = useCallback(() => {
    setSessionWarning({
      type: 'expired',
      message: 'Your session has expired. Please log in again.'
    })
    clearTokens()
  }, [clearTokens])

  const extendSession = useCallback(async () => {
    try {
      await authService.refreshToken()
      setSessionWarning(null)
      SessionManager.extendSession()
      SessionManager.startSessionMonitoring(
        handleSessionExpiring,
        handleSessionExpired,
        authService.refreshToken
      )
    } catch (error) {
      console.error('Failed to extend session:', error)
      handleSessionExpired()
    }
  }, [handleSessionExpiring, handleSessionExpired])

  const dismissSessionWarning = useCallback(() => {
    setSessionWarning(null)
  }, [])

  const initAuth = useCallback(async () => {
    try {
      // Check if we have valid tokens
      if (TokenManager.isAuthenticated()) {
        // Verify token and get user info
        const userData = await authService.getCurrentUser()
        setUser(userData)
        
        // Start session monitoring
        SessionManager.startSessionMonitoring(
          handleSessionExpiring,
          handleSessionExpired,
          authService.refreshToken
        )
      } else {
        // Clear user if not authenticated
        setUser(null)
      }
    } catch (error) {
      console.error('Auth initialization error:', error)
      // Only clear tokens if it's an authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        clearTokens()
      } else {
        // For network errors, keep the tokens and retry later
        console.warn('Network error during auth init, will retry')
      }
    } finally {
      setLoading(false)
    }
  }, [clearTokens, handleSessionExpiring, handleSessionExpired])

  // Listen for token updates
  useEffect(() => {
    const handleTokenUpdate = () => {
      if (TokenManager.isAuthenticated()) {
        SessionManager.startSessionMonitoring(
          handleSessionExpiring,
          handleSessionExpired,
          authService.refreshToken
        )
      }
    }

    const handleTokenCleared = () => {
      setUser(null)
      setSessionWarning(null)
    }

    window.addEventListener('tokenUpdated', handleTokenUpdate)
    window.addEventListener('tokenCleared', handleTokenCleared)

    return () => {
      window.removeEventListener('tokenUpdated', handleTokenUpdate)
      window.removeEventListener('tokenCleared', handleTokenCleared)
    }
  }, [handleSessionExpiring, handleSessionExpired])

  useEffect(() => {
    initAuth()
  }, [initAuth])

  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null)
      setLoading(true)
      const response = await authService.login(email, password, rememberMe)

      setUser(response.user)
      
      SessionManager.startSessionMonitoring(
        handleSessionExpiring,
        handleSessionExpired,
        authService.refreshToken
      )

      showSuccess('Welcome back!', `Logged in as ${response.user.email}`)
      return response
    } catch (error) {
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.non_field_errors?.[0] ||
                          error.message ||
                          'Login failed. Please check your credentials.'
      setError(errorMessage)
      showError('Login Failed', errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData) => {
    try {
      setError(null)
      const response = await authService.register(userData)

      if (response.access && response.refresh) {
        setUser(response.user)
        
        SessionManager.startSessionMonitoring(
          handleSessionExpiring,
          handleSessionExpired,
          authService.refreshToken
        )
      }

      showSuccess(
        'Account Created!',
        'Welcome to the platform. Your account has been created successfully.'
      )
      return response
    } catch (error) {
      let errorMessage = 'Registration failed. Please try again.'
      
      if (error.response?.data) {
        errorMessage = error.response.data.email?.[0] ||
                      error.response.data.password?.[0] ||
                      error.response.data.username?.[0] ||
                      error.response.data.non_field_errors?.[0] ||
                      error.response.data.detail ||
                      errorMessage
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setError(errorMessage)
      showError('Registration Failed', errorMessage)
      throw error
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
      showSuccess('Logged Out', 'You have been successfully logged out.')
    } catch (error) {
      console.error('Logout error:', error)
      showError('Logout Error', 'There was an issue logging out.')
    } finally {
      clearTokens()
    }
  }

  const requestPasswordReset = async (email) => {
    try {
      setError(null)
      await authService.requestPasswordReset(email)
      showSuccess(
        'Email Sent',
        'Password reset instructions have been sent to your email.'
      )
      return { success: true, message: 'Password reset email sent successfully' }
    } catch (error) {
      const errorMessage = error.response?.data?.email?.[0] ||
                          error.response?.data?.error ||
                          'Failed to send password reset email'
      setError(errorMessage)
      showError('Reset Failed', errorMessage)
      throw error
    }
  }

  const confirmPasswordReset = async (
    uid,
    token,
    newPassword,
    confirmPassword
  ) => {
    try {
      setError(null)
      await authService.confirmPasswordReset(
        uid,
        token,
        newPassword,
        confirmPassword
      )
      showSuccess(
        'Password Reset',
        'Your password has been reset successfully. You can now log in.'
      )
      return { success: true, message: 'Password reset successfully' }
    } catch (error) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.new_password?.[0] ||
                          'Failed to reset password'
      setError(errorMessage)
      showError('Reset Failed', errorMessage)
      throw error
    }
  }

  const updateProfile = async (profileData) => {
    try {
      setError(null)
      const updatedUser = await authService.updateProfile(profileData)
      setUser(updatedUser)
      showSuccess('Profile Updated', 'Your profile has been updated successfully.')
      return updatedUser
    } catch (error) {
      const errorMessage = error.response?.data?.error ||
                          'Failed to update profile'
      setError(errorMessage)
      showError('Update Failed', errorMessage)
      throw error
    }
  }

  const clearError = () => setError(null)

  // New security methods
  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      setError(null)
      await authService.changePassword(currentPassword, newPassword, confirmPassword)
      return { success: true, message: 'Password changed successfully' }
    } catch (error) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.current_password?.[0] ||
                          error.response?.data?.new_password?.[0] ||
                          error.message ||
                          'Failed to change password'
      setError(errorMessage)
      throw error
    }
  }



  const value = {
    user,
    loading,
    error,
    sessionWarning,
    login,
    register,
    logout,
    requestPasswordReset,
    confirmPasswordReset,
    updateProfile,
    changePassword,

    extendSession,
    dismissSessionWarning,
    clearError,
    isAuthenticated: !!user && TokenManager.isAuthenticated(),
    token: TokenManager.getAccessToken(),
    authStatus: authService.getAuthStatus(),
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext